// Reads rarely need more than this to come back; writes get more slack since a
// slow mutation still landing is worse to abandon than a slow read.
const DEFAULT_READ_TIMEOUT_MS = 20000;
const DEFAULT_WRITE_TIMEOUT_MS = 60000;

export default class HttpClient {

  private static readonly ROOT_URL = `${import.meta.env.VITE_LEEDPULSE_API_URL}/api/v1`;

  // Enforces a max delay via AbortController so a hung request doesn't leave a caller
  // waiting forever, combined with a caller-supplied `signal` (e.g. to cancel a read
  // that became stale). Only OUR timer firing surfaces as the French timeout message;
  // a caller-signal abort is rethrown untouched (the caller already knows why it
  // cancelled its own request).
  private static async safeFetch(
    url: string,
    options: RequestInit,
    timeoutMs: number,
    signal?: AbortSignal
  ): Promise<Response> {
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    const onCallerAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', onCallerAbort);
    }

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        if (!timedOut) {
          // Caller's own `signal` aborted (e.g. a stale request cancelled) — not our timeout.
          throw error;
        }
        throw new Error('La requête a pris trop de temps. Vérifiez votre connexion et réessayez.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onCallerAbort);
    }
  }

  // get
  public static async get(
    endpoint: string,
    bearerToken?: string,
    additionalHeaders = {},
    signal?: AbortSignal,
    timeoutMs: number = DEFAULT_READ_TIMEOUT_MS
  ): Promise<any> {
    const url = `${HttpClient.ROOT_URL}${endpoint}`;

    const response = await HttpClient.safeFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
        ...additionalHeaders,
      },
    }, timeoutMs, signal);

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData && errorData.data && errorData.data.msg) {
          errorMessage = errorData.data.msg;
        } else if (errorData && errorData.data && errorData.data.message) {
          errorMessage = errorData.data.message;
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (err) {
        // If the server does not return valid JSON, we keep the basic message
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  public static async post(
    endpoint: string,
    payload: any,
    bearerToken: string,
    isFormData = false,
    additionalHeaders = {},
    messagesByStatus = new Map<number, string>(),
    signal?: AbortSignal,
    timeoutMs: number = DEFAULT_WRITE_TIMEOUT_MS
  ): Promise<any> {
    const url = `${HttpClient.ROOT_URL}${endpoint}`;

    const headers = isFormData
      ? {
        Authorization: `Bearer ${bearerToken}`,
        ...additionalHeaders,
      }
      : {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
        ...additionalHeaders,
      };

    const response: Response = await HttpClient.safeFetch(url, {
      method: 'POST',
      headers: headers,
      body: isFormData ? (payload as FormData) : JSON.stringify(payload),
    }, timeoutMs, signal);

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;

      try {
        const errorData = await response.json();
        const manualMessage = messagesByStatus.get(errorData.data.status);
        if (manualMessage) {
          errorMessage = manualMessage;
        } else {
          if (errorData && errorData.data && errorData.data.msg) {
            errorMessage = errorData.data.msg;
          } else if (errorData && errorData.data && errorData.data.message) {
            errorMessage = errorData.data.message;
          } else if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        }
      } catch (err) {
        console.error('Impossible de parser la réponse d\'erreur:', err);
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Delete method
  public static async delete(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {},
    signal?: AbortSignal,
    timeoutMs: number = DEFAULT_WRITE_TIMEOUT_MS
  ): Promise<any> {
    const url = `${HttpClient.ROOT_URL}${endpoint}`;
    const response = await HttpClient.safeFetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
        ...additionalHeaders,
      },
      body: JSON.stringify(payload),
    }, timeoutMs, signal);

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData && errorData.data && errorData.data.msg) {
          errorMessage = errorData.data.msg;
        } else if (errorData && errorData.data && errorData.data.message) {
          errorMessage = errorData.data.message;
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (err) {
        // If the server does not return valid JSON, we keep the basic message
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  // PUT method
  public static async put(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {},
    signal?: AbortSignal,
    timeoutMs: number = DEFAULT_WRITE_TIMEOUT_MS
  ): Promise<any> {
    const url = `${HttpClient.ROOT_URL}${endpoint}`;
    const response = await HttpClient.safeFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
        ...additionalHeaders,
      },
      body: JSON.stringify(payload),
    }, timeoutMs, signal);

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData && errorData.data && errorData.data.msg) {
          errorMessage = errorData.data.msg;
        } else if (errorData && errorData.data && errorData.data.message) {
          errorMessage = errorData.data.message;
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (err) {
        // If the server does not return valid JSON, we keep the basic message
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * POST request that returns a Blob (for file downloads)
   */
  public static async postBlob(
    endpoint: string,
    payload: any,
    bearerToken: string,
    additionalHeaders = {},
    timeoutMs: number = DEFAULT_WRITE_TIMEOUT_MS
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `${HttpClient.ROOT_URL}${endpoint}`;

    const response = await HttpClient.safeFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
        ...additionalHeaders,
      },
      body: JSON.stringify(payload),
    }, timeoutMs);

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData && errorData.data && errorData.data.msg) {
          errorMessage = errorData.data.msg;
        } else if (errorData && errorData.data && errorData.data.message) {
          errorMessage = errorData.data.message;
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (err) {
        // If the server does not return valid JSON, we keep the basic message
      }

      throw new Error(errorMessage);
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download'; // default name
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }
}
